import { useEffect, useRef } from 'react';
import './WaterPledgeAnimation.css';

export default function WaterPledgeAnimation({ playerRef, opponentRef, onComplete }) {
  const animRef = useRef(null);

  useEffect(() => {
    const attackerEl = playerRef.current;
    const defenderEl = opponentRef.current;
    const container = defenderEl.closest(".battle-container");
    
    if (!attackerEl || !defenderEl || !container) {
      onComplete();
      return;
    }

    // Get positions for both Pokemon
    const attackerRect = attackerEl.getBoundingClientRect();
    const defenderRect = defenderEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Calculate centers
    const attackerCenterX = attackerRect.left - containerRect.left + attackerRect.width / 2;
    const attackerCenterY = attackerRect.top - containerRect.top + attackerRect.height / 2;
    const defenderCenterX = defenderRect.left - containerRect.left + defenderRect.width / 2;
    const defenderCenterY = defenderRect.top - containerRect.top + defenderRect.height / 2;

    // Track all created elements for cleanup
    const elements = [];
    const timers = [];
    
    // PHASE 1: WATER PLEDGE PREPARATION (0-400ms)
    // Initial attacker animation - pledge preparation
    attackerEl.style.transition = 'transform 0.4s ease-in-out, filter 0.4s ease-in-out';
    attackerEl.style.transform = 'scale(1.1)';
    attackerEl.style.filter = 'brightness(1.3) drop-shadow(0 0 15px #00BFFF)';
    
    // Create water pledge aura around attacker
    const pledgeAura = document.createElement('div');
    pledgeAura.className = 'water-pledge-aura';
    pledgeAura.style.left = `${attackerCenterX}px`;
    pledgeAura.style.top = `${attackerCenterY}px`;
    container.appendChild(pledgeAura);
    elements.push(pledgeAura);
    
    // Create water energy particles around attacker
    for (let i = 0; i < 16; i++) {
      const energyTimer = setTimeout(() => {
        const particle = document.createElement('div');
        particle.className = 'water-pledge-energy-particle';
        
        const particleAngle = (i / 16) * Math.PI * 2;
        const particleDistance = 35 + Math.random() * 20;
        particle.style.left = `${attackerCenterX + Math.cos(particleAngle) * particleDistance}px`;
        particle.style.top = `${attackerCenterY + Math.sin(particleAngle) * particleDistance}px`;
        particle.style.setProperty('--particle-delay', `${i * 0.03}s`);
        
        container.appendChild(particle);
        elements.push(particle);
        
        // Auto-remove particle
        const particleRemoveTimer = setTimeout(() => {
          if (particle.isConnected) {
            particle.remove();
          }
        }, 600);
        timers.push(particleRemoveTimer);
      }, i * 20);
      timers.push(energyTimer);
    }

    // PHASE 2: GROUND WATER BUILDUP (400-800ms)
    const buildupTimer = setTimeout(() => {
      // Create multiple ground water buildup points closer to opponent
      const buildupPoints = 5;
      
      for (let i = 0; i < buildupPoints; i++) {
        const buildupPointTimer = setTimeout(() => {
          // Focus buildup points more toward the opponent (70-95% of the way)
          const progress = 0.7 + (i / (buildupPoints - 1)) * 0.25;
          const pointX = attackerCenterX + (defenderCenterX - attackerCenterX) * progress;
          const pointY = attackerCenterY + (defenderCenterY - attackerCenterY) * progress;
          
          // Create ground water buildup indicator
          const buildup = document.createElement('div');
          buildup.className = 'water-pledge-ground-buildup';
          buildup.style.left = `${pointX}px`;
          buildup.style.top = `${pointY + 30}px`; // Slightly below Pokemon level
          buildup.style.setProperty('--buildup-delay', `${i * 0.1}s`);
          
          container.appendChild(buildup);
          elements.push(buildup);
          
          // Create ripple effect at buildup point
          const ripple = document.createElement('div');
          ripple.className = 'water-pledge-ground-ripple';
          ripple.style.left = `${pointX}px`;
          ripple.style.top = `${pointY + 30}px`;
          ripple.style.setProperty('--ripple-delay', `${i * 0.1 + 0.2}s`);
          
          container.appendChild(ripple);
          elements.push(ripple);
        }, i * 80);
        timers.push(buildupPointTimer);
      }
    }, 400);
    timers.push(buildupTimer);
        
    // PHASE 3: WATER GEYSER ERUPTIONS (800-1400ms)
    const eruptionTimer = setTimeout(() => {
      // Create multiple water geysers erupting closer to opponent
      const geyserPoints = 6;
      
      for (let i = 0; i < geyserPoints; i++) {
        const geyserTimer = setTimeout(() => {
          // Focus geysers around the opponent area (60-100% of the way)
          const progress = 0.6 + (i / (geyserPoints - 1)) * 0.4;
          const geyserX = attackerCenterX + (defenderCenterX - attackerCenterX) * progress;
          const geyserY = attackerCenterY + (defenderCenterY - attackerCenterY) * progress;
          
          // Create main water geyser
          const geyser = document.createElement('div');
          geyser.className = 'water-pledge-geyser';
          geyser.style.left = `${geyserX}px`;
          geyser.style.top = `${geyserY + 20}px`;
          geyser.style.setProperty('--geyser-height', `${80 + Math.random() * 40}px`);
          geyser.style.setProperty('--geyser-delay', `${i * 0.05}s`);
          
          container.appendChild(geyser);
          elements.push(geyser);
          
          // Create water spray particles around geyser
          for (let j = 0; j < 12; j++) {
            const sprayTimer = setTimeout(() => {
              const spray = document.createElement('div');
              spray.className = 'water-pledge-spray-particle';
              
              const sprayAngle = Math.random() * Math.PI * 2;
              const sprayDistance = 15 + Math.random() * 25;
              spray.style.left = `${geyserX + Math.cos(sprayAngle) * sprayDistance}px`;
              spray.style.top = `${geyserY + Math.sin(sprayAngle) * sprayDistance}px`;
              
              spray.style.setProperty('--spray-velocity-x', `${(Math.random() - 0.5) * 40}px`);
              spray.style.setProperty('--spray-velocity-y', `${-30 - Math.random() * 20}px`);
              spray.style.setProperty('--spray-size', `${3 + Math.random() * 5}px`);
              
              container.appendChild(spray);
              elements.push(spray);
              
              const sprayRemoveTimer = setTimeout(() => {
                if (spray.parentNode) {
                  spray.remove();
                }
              }, 800);
              timers.push(sprayRemoveTimer);
            }, j * 30);
            timers.push(sprayTimer);
          }
        }, i * 100);
        timers.push(geyserTimer);
      }
      
      // Create final massive geyser at opponent location
      const finalGeyserTimer = setTimeout(() => {
        const finalGeyser = document.createElement('div');
        finalGeyser.className = 'water-pledge-final-geyser';
        finalGeyser.style.left = `${defenderCenterX}px`;
        finalGeyser.style.top = `${defenderCenterY + 10}px`;
        
        container.appendChild(finalGeyser);
        elements.push(finalGeyser);
        
        // Make defender react with water hit animation
        defenderEl.classList.add('water-pledge-hit');
        defenderEl.style.filter = 'brightness(1.4) hue-rotate(200deg) saturate(1.5)';
        defenderEl.style.transform = 'translateY(-10px) scale(0.95)';
        
        // Create massive water explosion around opponent
        const explosion = document.createElement('div');
        explosion.className = 'water-pledge-explosion';
        explosion.style.left = `${defenderCenterX}px`;
        explosion.style.top = `${defenderCenterY}px`;
        
        container.appendChild(explosion);
        elements.push(explosion);
        
        // Create water burst particles
        for (let i = 0; i < 25; i++) {
          const burstParticle = document.createElement('div');
          burstParticle.className = 'water-pledge-burst-particle';
          
          const burstAngle = Math.random() * Math.PI * 2;
          const burstDistance = 20 + Math.random() * 50;
          burstParticle.style.left = `${defenderCenterX + Math.cos(burstAngle) * burstDistance}px`;
          burstParticle.style.top = `${defenderCenterY + Math.sin(burstAngle) * burstDistance}px`;
          
          burstParticle.style.setProperty('--burst-velocity-x', `${(Math.random() - 0.5) * 80}px`);
          burstParticle.style.setProperty('--burst-velocity-y', `${-40 - Math.random() * 40}px`);
          burstParticle.style.setProperty('--burst-size', `${4 + Math.random() * 8}px`);
          
          container.appendChild(burstParticle);
          elements.push(burstParticle);
          
          const burstRemoveTimer = setTimeout(() => {
            if (burstParticle.parentNode) {
              burstParticle.remove();
            }
          }, 1000);
          timers.push(burstRemoveTimer);
        }
        
        // Reset defender after hit effect
        const defenderResetTimer = setTimeout(() => {
          defenderEl.classList.remove('water-pledge-hit');
          defenderEl.style.filter = '';
          defenderEl.style.transform = '';
        }, 600);
        timers.push(defenderResetTimer);
        
      }, 500); // Final geyser timing
      timers.push(finalGeyserTimer);
      
    }, 800); // Eruption timing
    timers.push(eruptionTimer);
    
    // PHASE 4: RESET ATTACKER (1400-1800ms)
    const resetTimer = setTimeout(() => {
      // Reset attacker to normal state
      attackerEl.style.transition = 'transform 0.4s ease-out, filter 0.4s ease-out';
      attackerEl.style.transform = '';
      attackerEl.style.filter = '';
    }, 1400);
    timers.push(resetTimer);

    // Complete animation and cleanup
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 1800);
    timers.push(completeTimer);
    
    // Cleanup function
    return () => {
      // Clear all timers
      timers.forEach(timer => clearTimeout(timer));
      
      // Reset attacker
      if (attackerEl) {
        attackerEl.style.transition = '';
        attackerEl.style.transform = '';
        attackerEl.style.filter = '';
      }
      
      // Reset defender
      if (defenderEl) {
        defenderEl.classList.remove('water-pledge-hit');
        defenderEl.style.filter = '';
        defenderEl.style.transform = '';
      }
      
      // Remove all created elements
      elements.forEach(element => {
        if (element && element.parentNode) {
          element.remove();
        }
      });
      
      // Remove any remaining effects
      const remainingElements = container.querySelectorAll(
        '.water-pledge-aura, .water-pledge-energy-particle, .water-pledge-ground-buildup, ' +
        '.water-pledge-ground-ripple, .water-pledge-geyser, .water-pledge-spray-particle, ' +
        '.water-pledge-final-geyser, .water-pledge-explosion, .water-pledge-burst-particle'
      );
      remainingElements.forEach(el => el.remove());
    };
  }, [playerRef, opponentRef, onComplete]);
  
  return null;
}

WaterPledgeAnimation.moveType = "water";
